/** @jsxImportSource @emotion/react */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import * as d3 from 'd3'
import { ChartTooltip, ChartTooltipItem } from '../ChartTooltip'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { getConsequencePalette, roundForDisplay } from '../utils/utils'
import {
  parameterKeyTitleConverter,
  useLayerDataFetcher,
} from '../../RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { capitalizeFirstLetter } from '@app/utils/strings.utils'
import { AxisCalculator } from '../utils/AxisCalculator'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'
export type DataPoint = {
  [key: string]: string | number
} & {
  total: number
  unspecified?: number
  potential?: number
  exposed?: number
  insignificant?: number
  low?: number
  minor?: number
  moderate?: number
  major?: number
  high?: number
}
const fallbackData: DataPoint[] = [
  { year: 2000, exposed: 0, major: 0, moderate: 0, minor: 0, total: 0 },
  { year: 2020, exposed: 1, major: 0.5, moderate: 0.2, minor: 0.1, total: 0 },
  { year: 2040, exposed: 3, major: 1.5, moderate: 1, minor: 0.5, total: 0 },
  { year: 2060, exposed: 4, major: 2.5, moderate: 1.5, minor: 0.8, total: 0 },
  { year: 2080, exposed: 6, major: 3.5, moderate: 1, minor: 0.5, total: 0 },
  { year: 2100, exposed: 7, major: 4, moderate: 2, minor: 1, total: 0 },
]
export const MakeProjectedRiskChartSvg = ({
  data,
  svgElement,
  width = 900,
  parameter,
  vulnerabilityList,
  consequencePalette,
  parameterTitle,
  parameterUnit,
  yAxisTitle,
  xAxisMode = 'ari',
  updateTooltip,
}: {
  data: DataPoint[]
  svgElement: HTMLDivElement | null
  width: number
  parameter: string
  vulnerabilityList: string[]
  consequencePalette: { [key: string]: string }
  parameterTitle: string
  parameterUnit: string
  yAxisTitle: string
  xAxisMode?: 'ari' | 'aep'
  updateTooltip?: (args: { show: boolean; data: DataPoint }) => void
}) => {
  if (!data || data.length === 0 || vulnerabilityList.length === 0) return

  // strong margin right because text gets cut off
  const margin = { top: 12, right: 80, bottom: 40, left: 52 }
  const height = Math.max(width / 3, 200)
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  d3.select(svgElement).selectAll('svg').remove()
  const svg = d3
    .select(svgElement)
    .append('svg')
    .attr('version', '1.1')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('width', width)
    .attr('height', height)
    .attr('font-family', 'Inter')
    .attr('font-size', '8pt')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('color', '#8392a1')
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  let filteredVulnerabiltyList = [...vulnerabilityList]
  if (vulnerabilityList.length === 2) {
    // remove total from vulnerability list if there are only one vulnerabilities
    filteredVulnerabiltyList = vulnerabilityList.filter((v) => v !== 'total')
  }

  // -- EP CURVE (loss on X, AEP on Y) -----------------------------------------
  if (xAxisMode === 'aep' && parameter.includes('ari')) {
    const aepForAri = (ari: number) => 1 - Math.exp(-1 / ari)
    const aepValues = data.map((d) => aepForAri(d[parameter] as number))
    const minAep = Math.min(...aepValues)

    const allLossValues = filteredVulnerabiltyList.flatMap((v) =>
      data.map((d) => Number(d[v] ?? 0)),
    )
    const xCalc = new AxisCalculator([...allLossValues, 0])

    const xScale = d3.scaleLinear().domain([0, xCalc.domain[1]]).range([0, innerWidth])

    // Y: log scale, 100% at top -> approaching 0 at bottom
    const yBottom = Math.min(minAep * 0.5, 0.001)
    const yScale = d3.scaleLog().domain([1.0, yBottom]).range([0, innerHeight])

    const logTicks = [1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01, 0.005, 0.001].filter(
      (t) => t <= 1.0 && t >= yBottom,
    )

    // X axis -> loss
    svg
      .append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(xCalc.tickCount)
          .tickFormat((t) => xCalc.roundValue(t.valueOf()))
          .tickPadding(8)
          .tickSizeOuter(6),
      )
      .append('text')
      .attr('fill', '#8392a1')
      .attr('text-anchor', 'middle')
      .attr('x', innerWidth / 2)
      .attr('y', margin.bottom - 4)
      .text(yAxisTitle)
      .attr('font-family', 'Inter')

    // Y axis -> AEP
    svg
      .append('g')
      .call(
        d3
          .axisLeft(yScale)
          .tickValues(logTicks)
          .tickSizeOuter(0)
          .tickSize(0)
          .tickFormat((d) => {
            const pct = (d as number) * 100
            return pct >= 1 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`
          }),
      )
      .append('text')
      .attr('fill', '#8392a1')
      .attr('font-family', 'Inter')
      .attr('text-anchor', 'middle')
      .attr('font-size', '10.67px')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 10)
      .attr('x', -innerHeight / 2)
      .text('Annual Exceedance Probability (AEP)')

    svg.selectAll('.domain').attr('stroke', '#53687e').attr('stroke-width', 0)

    // Horizontal grid lines at each log tick
    logTicks.forEach((t) => {
      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', yScale(t))
        .attr('y2', yScale(t))
        .attr('stroke', 'lightgrey')
    })

    // Lines + dots per vulnerability -> keep original ARI order so AEP is monotone
    for (const vuln of filteredVulnerabiltyList) {
      const points = data
        .map((d, i) => ({ loss: Number(d[vuln] ?? 0), aep: aepValues[i] }))
        .filter((p) => p.loss > 0 && p.aep >= yBottom)

      if (points.length < 2) continue

      svg
        .append('path')
        .datum(points)
        .attr('fill', 'none')
        .attr('stroke', consequencePalette[vuln] ?? '#000')
        .attr('stroke-width', 1.5)
        .attr(
          'd',
          d3
            .line<{ loss: number; aep: number }>()
            .x((p) => xScale(p.loss))
            .y((p) => yScale(p.aep)),
        )

      svg
        .selectAll(`.dots-${vuln}`)
        .data(points)
        .enter()
        .append('circle')
        .attr('cx', (p) => xScale(p.loss))
        .attr('cy', (p) => yScale(p.aep))
        .attr('r', 2)
        .attr('stroke', consequencePalette[vuln] ?? '#000')
        .attr('stroke-width', 1.5)
        .attr('fill', 'white')
    }

    // Labels along the x-axis at each line's terminal (highest-loss) point
    const epXLabels = filteredVulnerabiltyList
      .map((vuln) => {
        const points = data
          .map((d, i) => ({ loss: Number(d[vuln] ?? 0), aep: aepValues[i] }))
          .filter((p) => p.loss > 0 && p.aep >= yBottom)
        const last = points[points.length - 1]
        return { vulnerability: vuln, xVal: last ? xScale(last.loss) : null }
      })
      .filter((d) => d.xVal !== null)
      .toSorted((a, b) => (a.xVal ?? 0) - (b.xVal ?? 0))

    // Spread labels that are too close along x
    let epLabelLimit = 10
    while (
      epXLabels.some((v, i) =>
        epXLabels.some((v2, i2) => Math.abs((v2.xVal ?? 0) - (v.xVal ?? 0)) < 30 && i !== i2),
      )
    ) {
      epXLabels.forEach((v, i) => {
        if (i > 0 && Math.abs((epXLabels[i - 1].xVal ?? 0) - (v.xVal ?? 0)) < 30) {
          epXLabels[i - 1].xVal = (epXLabels[i - 1].xVal ?? 0) - 6
          epXLabels[i].xVal = (epXLabels[i].xVal ?? 0) + 6
        }
      })
      if (--epLabelLimit <= 0) break
    }

    svg
      .selectAll('.ep-x-label')
      .data(epXLabels)
      .enter()
      .append('text')
      .attr('class', 'ep-x-label')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => d.xVal ?? 0)
      .attr('y', innerHeight + 12)
      .attr('fill', (d) => consequencePalette[d.vulnerability])
      .text((d) => capitalizeFirstLetter(d.vulnerability))

    // Invisible overlay: find the nearest data point by AEP (y position) on mousemove.
    // Each data point shares one AEP across all vulnerabilities, so a single tooltip per ARI works.
    if (updateTooltip) {
      const hoverPoints = data
        .map((d, i) => ({ datum: d, yPos: yScale(aepValues[i]), aep: aepValues[i] }))
        .filter((p) => p.aep >= yBottom)

      if (hoverPoints.length > 0) {
        svg
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', innerWidth)
          .attr('height', innerHeight)
          .attr('fill', 'transparent')
          .on('mouseleave', () => {
            updateTooltip({ show: false, data: hoverPoints[0].datum })
          })
          .on('mousemove', function (event) {
            const [, my] = d3.pointer(event, this)
            let closest = hoverPoints[0]
            let minDist = Math.abs(my - closest.yPos)
            for (const p of hoverPoints) {
              const dist = Math.abs(my - p.yPos)
              if (dist < minDist) {
                minDist = dist
                closest = p
              }
            }
            updateTooltip({ show: true, data: closest.datum })
          })
      }
    }

    return
  }

  // -- STANDARD ARI CHART (loss on Y, return period on X) ---------------------
  const yCalculator = new AxisCalculator([...data.map((d) => d.total ?? 0), 0])

  const isPercentageValue = parameter.includes('aep')
  const showPercent = isPercentageValue ? '%' : ''
  const useLogScale = parameter.includes('aep') || parameter.includes('ari')
  const shouldReverse = ['aep'].includes(parameter)

  let x = null
  if (useLogScale) {
    x = d3
      .scaleLog()
      .domain(
        d3.extent(data, (d) => {
          if (typeof d[parameter] === 'string') {
            return parseFloat(d[parameter] as string)
          }
          return d[parameter] as number
        }) as [number, number],
      )
      .range(shouldReverse ? [innerWidth, 0] : [0, innerWidth])
  } else {
    x = d3
      .scaleLinear()
      .domain(
        d3.extent(data, (d) => {
          if (typeof d[parameter] === 'string') {
            return parseFloat(d[parameter] as string)
          }
          return d[parameter] as number
        }) as [number, number],
      )
      .range(shouldReverse ? [innerWidth, 0] : [0, innerWidth])
  }

  const y = d3.scaleLinear().domain(yCalculator.domain.reverse()).nice(8).range([0, innerHeight])
  const line = (yAccessor: (d: DataPoint) => number, _: string) =>
    d3
      .line<DataPoint>()
      .defined((d) => !isNaN(yAccessor(d)))
      .x((d) => x(d[parameter] as number))
      .y((d) => y(yAccessor(d)))

  svg
    .append('g')
    .attr('transform', `translate(${0},${innerHeight})`)
    // will need to match ticks to parameter's values
    .call(
      d3
        .axisBottom(x)
        .tickSizeOuter(6)
        .tickValues(data.map((d) => d[parameter] as number))
        .tickFormat((d) => {
          if (isPercentageValue) {
            return `${d}${showPercent}`
          }
          if (Number.isInteger(d)) {
            return `${d.toString()}${showPercent}`
          } else if (d) {
            return `${d.toLocaleString(undefined, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}${showPercent}`
          } else {
            return `${d}${showPercent}`
          }
        })
        .tickPadding(8),
    )
    .append('text') // x label
    .attr('fill', '#8392a1')
    .attr('text-anchor', 'middle')
    .attr('x', innerWidth / 2)
    .attr('y', margin.bottom - 4)
    .text(`${parameterTitle} ${parameterUnit ?? ''}`)
    .attr('font-family', 'Inter')

  svg
    .append('g')
    .call(
      d3
        .axisLeft(y)
        .tickSizeOuter(0)
        .tickSize(0)
        .ticks(yCalculator.tickCount)
        .tickFormat((t) => yCalculator.roundValue(t.valueOf())),
    )
    .append('text') // y label
    .attr('fill', '#8392a1')
    .attr('font-family', 'Inter')
    .attr('text-anchor', 'middle')
    .attr('font-size', '10.67px')
    .attr('transform', 'rotate(-90)')
    .attr('y', -margin.left + 10)
    .attr('x', -innerHeight / 2)
    .text(yAxisTitle)

  svg.selectAll('.domain').attr('stroke', '#53687e').attr('stroke-width', 0)

  // Append lines for y-axis ticks
  y.ticks(Math.min(8, y.ticks().length)).forEach((tick) => {
    svg
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', y(tick))
      .attr('y2', y(tick))
      .attr('stroke', 'lightgrey')
      .attr('stroke-line')
  })

  for (const vulnerability of filteredVulnerabiltyList) {
    if (data.length === 1) {
      svg
        .append('circle')
        .attr('cx', x(data[0][parameter] as number))
        .attr('cy', y(Number(data[0][vulnerability]) || 0))
        .attr('r', 3)
        .attr('fill', consequencePalette[vulnerability])
      svg
        .append('text')
        .attr('x', x(data[0][parameter] as number) + 5)
        .attr('y', y(Number(data[0][vulnerability]) || 0) + 3)
        .attr('stroke', 'white')
        .attr('stroke-width', 4)
        .text(capitalizeFirstLetter(vulnerability))
      svg
        .append('text')
        .attr('x', x(data[0][parameter] as number) + 5)
        .attr('y', y(Number(data[0][vulnerability]) || 0) + 3)
        .attr('fill', consequencePalette[vulnerability])
        .text(capitalizeFirstLetter(vulnerability))
    } else if (data.length > 1) {
      svg
        .append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', consequencePalette[vulnerability] ?? '#000')
        .attr('stroke-width', 1.5)
        .attr(
          'd',
          line((d) => {
            return Number(d?.[vulnerability] ?? 0)
          }, consequencePalette[vulnerability] ?? '#000'),
        )
      svg
        .selectAll('circle.' + vulnerability)
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => x(d[parameter] as number))
        .attr('cy', (d) => y(Number(d[vulnerability]) || 0))
        .attr('r', 2)
        .attr('stroke', consequencePalette[vulnerability] ?? '#000')
        .attr('stroke-width', 1.5)
        .attr('fill', 'white')
    }
  }
  if (data.length > 1) {
    const textYValues = filteredVulnerabiltyList
      .map((vulnerability) => ({
        vulnerability,
        yVal:
          y(
            Number(shouldReverse ? data[0][vulnerability] : data[data.length - 1][vulnerability]) ||
              0,
          ) + 3,
      }))
      .toSorted((a, b) => a.yVal - b.yVal)

    let limit = 10
    // space out the text labels
    while (
      textYValues.some((v, i) =>
        textYValues.some((v2, i2) => Math.abs(v2.yVal - v.yVal) < 12 && i !== i2),
      )
    ) {
      textYValues.forEach((v, i) => {
        if (i > 0 && Math.abs(textYValues[i - 1].yVal - v.yVal) < 12) {
          textYValues[i - 1].yVal -= 3
          textYValues[i].yVal += 3
        }
      })
      limit -= 1
      if (limit <= 0) break
    }
    svg
      .selectAll('.legend-text')
      .data(textYValues)
      .enter()
      .append('text') // legend
      .attr('x', innerWidth + 10)
      .attr('y', (d) => d.yVal)
      .attr('fill', (d) => consequencePalette[d.vulnerability])
      .text((d) => capitalizeFirstLetter(d.vulnerability))
  }
  // Append invisible but hoverable vertical lines
  if (updateTooltip)
    svg
      .selectAll('.vertical-line')
      .data(data)
      .enter()
      .append('line')
      .attr('class', 'vertical-line')
      .attr('x1', (d) => x(d[parameter] as number))
      .attr('x2', (d) => x(d[parameter] as number))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#00000000')
      .attr('stroke-width', 40)
      .on('mouseleave', (_, d) => {
        updateTooltip({ show: false, data: d })
      })
      .on('mouseenter', (_, d) => {
        updateTooltip({ show: true, data: d })
      })
}
export const ProjectedRiskChart = ({
  data = fallbackData,
  parameter,
  vulnerabilityList,
  parameterUnit,
  assetId,
  yAxisTitle,
  yAxisUnit,
  xAxisMode = 'ari',
}: {
  data: DataPoint[]
  parameter: string
  vulnerabilityList: string[]
  parameterUnit: string
  assetId: string
  yAxisTitle: string
  yAxisUnit: string | undefined
  xAxisMode?: 'ari' | 'aep'
}) => {
  const layerData = useLayerDataFetcher()
  const parameterTitle = parameterKeyTitleConverter(parameter, layerData)
  const currencySymbol = useAtomValue(currencySymbolAtom)

  const isPercentageValue = parameter.includes('aep')
  const showPercent = isPercentageValue ? '%' : ''
  const svgRef = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState<number | null>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipItems, setTooltipItems] = useState<ChartTooltipItem[]>([])
  const [tooltipHeader, setTooltipHeader] = useState('')
  const vulnerabilityPalette = useSelector(
    (state: RootState) => state.riskMap.legendsData['vulnerability'],
  )
  const consequencePalette = useMemo(
    () => getConsequencePalette(vulnerabilityPalette),
    [vulnerabilityPalette],
  )
  useEffect(() => {
    if (!svgRef.current) return
    const resizeObserver = new ResizeObserver((event) => {
      // Round width to nearest 50
      const newWidth = Math.round(event[0].contentBoxSize[0].inlineSize / 50) * 50
      setWidth(newWidth)
    })
    resizeObserver.observe(svgRef.current)
  }, [])
  const updateTooltip = useCallback(
    ({ show, data }: { show: boolean; data: DataPoint }) => {
      setShowTooltip(show)
      setTooltipHeader(
        `${data[parameter]}${parameterUnit ? parameterUnit : showPercent} ${parameterTitle}`,
      )
      if (data && consequencePalette) {
        const items = Object.keys(data)
          .filter((key) => key == 'total' || vulnerabilityList.includes(key))
          .sort((a, b) => {
            const order: { [key: string]: number } = {
              unspecified: 0,
              potential: 1,
              insignificant: 2,
              exposed: 3,
              low: 4,
              minor: 5,
              moderate: 6,
              major: 7,
              high: 8,
            }
            return (order[a as keyof DataPoint] ?? 0) - (order[b as keyof DataPoint] ?? 0)
          })
          .map((key) => {
            const item = parseFloat(`${data[key] ?? 0}`)
            const label =
              yAxisTitle == `Value (${currencySymbol})`
                ? `${currencySymbol}${roundForDisplay(item, '$')}`
                : `${roundForDisplay(item, yAxisUnit)} ${yAxisTitle || ''}`
            return {
              text: `${key[0].toUpperCase() + key.slice(1)}: ${label}`,
              color: consequencePalette[key] ?? '#000',
            }
          })

        let filteredItems = [...items]
        if (filteredItems.length === 2)
          filteredItems = items.filter((item) => !item.text.startsWith('Total:'))
        setTooltipItems(filteredItems)
      }
    },
    [
      parameter,
      parameterUnit,
      showPercent,
      parameterTitle,
      consequencePalette,
      vulnerabilityList,
      yAxisTitle,
      currencySymbol,
      yAxisUnit,
    ],
  )
  useEffect(() => {
    if (!width || !consequencePalette) return
    MakeProjectedRiskChartSvg({
      data,
      svgElement: svgRef.current,
      width,
      parameter,
      vulnerabilityList,
      consequencePalette,
      parameterTitle,
      parameterUnit,
      yAxisTitle,
      xAxisMode,
      updateTooltip,
    })
  }, [
    data,
    svgRef,
    width,
    parameter,
    vulnerabilityList,
    consequencePalette,
    parameterTitle,
    parameterUnit,
    assetId,
    yAxisTitle,
    xAxisMode,
    updateTooltip,
  ])
  return (
    <ChartTooltip header={tooltipHeader} show={showTooltip} items={tooltipItems}>
      <div ref={svgRef}></div>
    </ChartTooltip>
  )
}
